import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import * as Dialog from "@radix-ui/react-dialog";

interface Props {
  returnFocus?: HTMLElement | null;
  title: string;
  description: string;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  dirty?: boolean;
  busy?: boolean;
  inactive?: boolean;
  layer?: number;
  className?: string;
}
export function AccessibleDialog({
  returnFocus,
  title,
  description,
  children,
  footer,
  onClose,
  dirty = false,
  busy = false,
  inactive = false,
  layer = 0,
  className = "",
}: Props) {
  const ref = useRef<HTMLDivElement>(null),
    guard = useRef<HTMLDivElement>(null);
  const [opener] = useState<HTMLElement | null>(
    () =>
      returnFocus ??
      (typeof document !== "undefined"
        ? (document.activeElement as HTMLElement)
        : null),
  );
  const [discard, setDiscard] = useState(false);
  const close = () => {
    if (busy || inactive) return;
    if (discard) {
      setDiscard(false);
      return;
    }
    if (dirty) setDiscard(true);
    else onClose();
  };
  useLayoutEffect(() => {
    if (ref.current) ref.current.inert = inactive;
  }, [inactive]);
  useEffect(() => {
    if (discard) guard.current?.focus();
  }, [discard]);
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className="trigger-overlay"
          style={{ zIndex: 2000 + layer * 20 }}
        />
        <Dialog.Content
          ref={ref}
          className={"trigger-dialog " + className}
          style={{ zIndex: 2001 + layer * 20 }}
          aria-modal={inactive ? false : true}
          aria-hidden={inactive || undefined}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            ref.current
              ?.querySelector<HTMLElement>("[data-initial-focus]")
              ?.focus();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            requestAnimationFrame(() => {
              if (opener?.isConnected && !opener.closest("[inert]"))
                opener.focus();
              else
                document
                  .querySelector<HTMLElement>(
                    "[role=dialog]:not([aria-hidden=true]) [data-initial-focus]",
                  )
                  ?.focus();
            });
          }}
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            close();
          }}
          onPointerDownOutside={(event) => {
            event.preventDefault();
            close();
          }}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <header className="trigger-dialog-header">
            <div>
              <Dialog.Title
                tabIndex={-1}
                data-initial-focus
                className="trigger-dialog-title"
              >
                {title}
              </Dialog.Title>
              <Dialog.Description>{description}</Dialog.Description>
            </div>
            <button
              type="button"
              className="trigger-icon-button"
              aria-label={"Закрити: " + title}
              disabled={busy}
              onClick={close}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                aria-hidden="true"
              >
                <path d="m6 6 12 12M18 6 6 18" />
              </svg>
            </button>
          </header>
          <div className="trigger-dialog-body">{children}</div>
          <footer className="trigger-dialog-footer">
            {discard ? (
              <div
                ref={guard}
                tabIndex={-1}
                role="alert"
                className="trigger-discard"
              >
                <strong>Відкинути незбережені зміни?</strong>
                <span>Чернетка залишиться, якщо продовжите редагування.</span>
                <div className="trigger-actions">
                  <button
                    type="button"
                    className="btn btn--secondary"
                    onClick={() => setDiscard(false)}
                  >
                    Продовжити редагування
                  </button>
                  <button
                    type="button"
                    className="btn btn--danger"
                    onClick={onClose}
                  >
                    Відкинути зміни
                  </button>
                </div>
              </div>
            ) : (
              (footer ?? (
                <button className="btn btn--secondary" onClick={close}>
                  Закрити
                </button>
              ))
            )}
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
